'use client';

import { useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { pushEvent } from '@/lib/analytics/core';
import type { FloatingVoiceHelperText, FloatingVoicePostCall } from './FloatingVoiceV1';
import type { TalkToBriSource } from './TalkToBriModal';

// Lazy-load the modal (and the three.js voice stack FloatingVoiceV1 pulls in)
// only once a CTA is actually clicked — zero modal JS ships in the initial
// bundle of any surface that merely renders a trigger button.
const TalkToBriModal = dynamic(() => import('./TalkToBriModal'), {
  ssr: false,
});

/**
 * Window event dispatched every time ANY trigger opens the Talk-to-Bri
 * modal. The hero's scripted phone demo (`HeroPhoneDemo`) listens for it to
 * silence its voiceover the moment a real voice session is about to start —
 * demo audio must never talk over the live modal.
 */
export const TALK_TO_BRI_MODAL_OPEN_EVENT = 'bizrnr:talk-to-bri-modal-open';

export interface TalkToBriModalTriggerProps {
  source: TalkToBriSource;
  /** Overrides the default platform demo agent (e.g. the enterprise sales agent). */
  agentId?: string;
  /** Overrides the modal's default Spark-CTA post-call card (e.g. enterprise
   *  pricing's `{ mode: 'restart' }`, which drops the self-serve CTA). */
  postCall?: FloatingVoicePostCall;
  /** Overrides the modal's default idle/connecting/listening/speaking/ended copy. */
  helperText?: FloatingVoiceHelperText;
  /** Render prop: receives the `open()` callback so callers own their own CTA markup. */
  children: (open: () => void) => ReactNode;
}

export default function TalkToBriModalTrigger({
  source,
  agentId,
  postCall,
  helperText,
  children,
}: TalkToBriModalTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    pushEvent('voice_widget_click', { source, surface: 'modal' });
    window.dispatchEvent(new Event(TALK_TO_BRI_MODAL_OPEN_EVENT));
    setIsOpen(true);
  };

  return (
    <>
      {children(open)}
      {isOpen && (
        <TalkToBriModal
          source={source}
          agentId={agentId}
          postCall={postCall}
          helperText={helperText}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
